// tabs/FilesTab.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, Divider, SimpleGrid, Card, Group, ActionIcon, Anchor, Stack, Tooltip } from '@mantine/core';
import {
  IconTrash, IconEye, IconFileTypePdf, IconFileTypeDoc, IconFileTypeXls,
  IconFileTypeZip, IconPhoto, IconFile, IconRefresh,
} from '@tabler/icons-react';
import { useCareCaseService, CareCaseFile } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { FileUploader } from '@/components/ccms-components/file-management';
import { UniversalFileViewer } from '@/components/ccms-components/file-management/components/UniversalFileViewer';
import ConfirmActionModal from './ConfirmActionModal';

interface FilesTabProps {
  caseID: number;
  active: boolean;
  onCaseChange?: () => void;
}

interface CompletedUpload {
  url: string;
  fileName: string;
  mimeType?: string;
}

const getFileIcon = (mimeType?: string | null) => {
  if (!mimeType) return IconFile;
  if (mimeType.startsWith('image/')) return IconPhoto;
  if (mimeType === 'application/pdf') return IconFileTypePdf;
  if (mimeType.includes('word') || mimeType.includes('document')) return IconFileTypeDoc;
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return IconFileTypeXls;
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return IconFileTypeZip;
  return IconFile;
};

export default function FilesTab({ caseID, active, onCaseChange }: FilesTabProps) {
  const careCaseService = useCareCaseService();

  const [files, setFiles]     = useState<CareCaseFile[]>([]);
  const [loading, setLoading] = useState(false);

  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction]   = useState<(() => Promise<void>) | null>(null);

  // ── Universal file viewer (preview) ────────────────────────────────────────
  const [viewerOpen, setViewerOpen]   = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const viewerFiles = useMemo(
    () => files.map((f) => ({
      id:       String(f.fileID),
      url:      f.url ?? undefined,
      fileName: f.name ?? `File ${f.fileID}`,
      mimeType: f.type ?? 'application/octet-stream',
      fileSize: (f as any).fileSize ?? (f as any).size ?? 0,
    })),
    [files]
  );

  const openPreview = (index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  const fetchFiles = useCallback(async () => {
    if (!caseID) return;
    setLoading(true);
    try {
      const result = await careCaseService.files(caseID);
      setFiles(result ?? []);
    } catch (e) {
      console.error('Error fetching files:', e);
      toast.error('Error', 'Could not load files.');
    } finally {
      setLoading(false);
    }
  }, [caseID, careCaseService]);

  useEffect(() => { if (active) fetchFiles(); }, [active, fetchFiles]);


  const handleUploadComplete = async (uploaded: CompletedUpload[]) => {
    try {
      await Promise.all(
        uploaded.map((file) =>
          careCaseService.addFile(caseID, {
            name: file.fileName,
            url:  file.url,
            type: file.mimeType,
          })
        )
      );
      await fetchFiles();
      onCaseChange?.();
      toast.success(
        'File Added',
        `${uploaded.length} file${uploaded.length !== 1 ? 's' : ''} uploaded successfully.`
      );
    } catch (e) {
      console.error(e);
      toast.error('Error', 'File uploaded but failed to attach it to the case.');
    }
  };

  const handleRemoveFile = (file: CareCaseFile) => {
    setConfirmMessage(`Remove this file${file.name ? ` (${file.name})` : ''} from the case?`);
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        await careCaseService.deleteFile(caseID, file.fileID);
        await fetchFiles();
        onCaseChange?.();
        toast.warning('File Removed', 'File removed from case.');
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove file.');
      } finally {
        setLoading(false);
      }
    });
    setConfirmOpen(true);
  };

  return (
    <Stack gap="md">
      <ConfirmActionModal
        opened={confirmOpen}
        title="Remove File"
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      <UniversalFileViewer
        opened={viewerOpen}
        onClose={() => setViewerOpen(false)}
        files={viewerFiles}
        initialIndex={viewerIndex}
        onIndexChange={setViewerIndex}
      />

        <Group justify="right" >
        <Tooltip label="Refresh files" withArrow>
          <ActionIcon
            variant="subtle"
            onClick={fetchFiles}
            loading={loading}
            size="lg"
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <FileUploader
        domain="CareCase"
        recordId={String(caseID)}
        multiple
        accept={['*/*']}
        onUploadComplete={handleUploadComplete}
        onUploadError={(err: any) => {
          console.error(err);
          toast.error('Upload Failed', err?.message ?? 'Could not upload file.');
        }}
      />

      <Divider my="md" />

      {files.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No files attached.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {files.map((f, index) => {
            const FileIcon = getFileIcon(f.type);
            return (
              <Card key={f.fileID} withBorder radius="md" padding="lg">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap" style={{ overflow: 'hidden' }}>
                    <FileIcon size={28} />
                    <div style={{ overflow: 'hidden' }}>
                      <Text fw={700} truncate>{f.name ?? `File ${f.fileID}`}</Text>
                      {f.url && (
                        <Anchor component="button" type="button" onClick={() => openPreview(index)} size="xs">
                          Preview
                        </Anchor>
                      )}
                      <Text size="xs" c="dimmed">
                        Uploaded: {f.uploadedDateTime ? String(f.uploadedDateTime) : 'Unknown'}
                      </Text>
                    </div>
                  </Group>
                  <Group gap={4} wrap="nowrap">
                    {f.url && (
                      <Tooltip label="Preview">
                        <ActionIcon variant="light" color="blue" onClick={() => openPreview(index)}>
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <ActionIcon variant="light" color="red" onClick={() => handleRemoveFile(f)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
    </Stack>
  );
}